import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const COLS = [
  { title: "Shop", links: [
    { label: "Solid Modal", to: "/collection/solid-modal" },
    { label: "Print Modal", to: "/collection/print-modal" },
    { label: "Chiffon", to: "/collection/chiffon" },
    { label: "New Arrivals", to: "/collection/all?filter=new" },
    { label: "Best Sellers", to: "/collection/all?filter=bestseller" },
  ]},
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [smsName, setSmsName] = useState("");
  const [smsPhone, setSmsPhone] = useState("");
  const { footer_logo, logo } = useSiteSettings();
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to LIRA. Check your inbox soon.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  const subscribeSms = async (e) => {
    e.preventDefault();
    if (!smsName.trim() || !smsPhone.trim()) return;
    try {
      await api.post("/sms/subscribe", { name: smsName, phone: smsPhone });
      toast.success("You're in! We'll text you the good stuff.");
      setSmsName("");
      setSmsPhone("");
    } catch {
      toast.error("Please enter a valid name and phone number.");
    }
  };

  return (
    <footer className="bg-[#560216] text-white mt-24 md:mt-32" data-testid="site-footer">
      <div className="container-lira py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 items-start">
          {COLS.map((col) => (
            <div key={col.title} className="flex flex-col gap-4">
              <h4 className="eyebrow text-white">{col.title}</h4>
              <ul className="flex flex-col gap-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-white/75 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="flex flex-col gap-4">
            <h4 className="eyebrow text-white">Text Messages</h4>
            <p className="text-sm text-white/75">Get early access to new drops, restocks, and members-only offers straight to your phone.</p>
            <form onSubmit={subscribeSms} className="flex flex-col gap-3" data-testid="footer-sms-form">
              <input
                type="text"
                value={smsName}
                onChange={(e) => setSmsName(e.target.value)}
                placeholder="Your name"
                className="bg-transparent outline-none text-sm text-white placeholder:text-white/60 border-b border-white/40 pb-2"
                data-testid="footer-sms-name"
              />
              <div className="flex items-center border-b border-white/40 pb-2">
                <input
                  type="tel"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="Phone number"
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/60"
                  data-testid="footer-sms-phone"
                />
                <button type="submit" className="eyebrow text-white" data-testid="footer-sms-submit">Subscribe</button>
              </div>
            </form>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="eyebrow text-white">Newsletter</h4>
            <p className="text-sm text-white/75">Be the first to know about new drops and private sales.</p>
            <form onSubmit={subscribe} className="flex items-center border-b border-white/40 pb-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/60"
                data-testid="footer-newsletter-input"
              />
              <button type="submit" className="eyebrow text-white" data-testid="footer-newsletter-submit">Join</button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/25 flex flex-col md:flex-row items-center justify-between gap-6">
          {footer_logo ? (
            <img src={footer_logo} alt="LIRA" className="h-14 w-auto object-contain" />
          ) : (
            <img src={logo || "/lira-logo.png"} alt="LIRA" className="h-14 w-auto object-contain brightness-0 invert" />
          )}
          <div className="flex items-center gap-5">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram strokeWidth={1} className="h-5 w-5" /></a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook strokeWidth={1} className="h-5 w-5" /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter"><Twitter strokeWidth={1} className="h-5 w-5" /></a>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/70">
            <span>VISA</span><span>·</span><span>MASTERCARD</span><span>·</span><span>AMEX</span><span>·</span><span>PAYPAL</span>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/70">
          <span>© {new Date().getFullYear()} LIRA. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/legal/refund-policy" className="hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
